import * as monaco from 'monaco-editor';
import type * as MonacoType from 'monaco-editor';

const LUA_API_DEFINITIONS: string[] = [];
export function registerLuaDefinition(entity: string): void {
    LUA_API_DEFINITIONS.push(entity);
}
export function getLuaDefinitions(): string {
    return LUA_API_DEFINITIONS.join('\n');
}

export class CodeEditor {

    private editor: monaco.editor.IStandaloneCodeEditor;

    public constructor(container: HTMLElement, initialCode: string = "", language: string = 'lua', readOnly: boolean = false) {
        initializeMonacoLuaIntelligence();

        // 4. Create Editor Instance
        this.editor = g_monacoModules.editor!.create(container, {
            value: initialCode,
            language: language,
            theme: 'vs-dark',
            automaticLayout: true,
            fontSize: 14,
            minimap: { enabled: false },
            suggestSelection: 'first',
            snippetSuggestions: 'inline',
            'semanticHighlighting.enabled': true,
            readOnly: readOnly,
            domReadOnly: readOnly,
            scrollBeyondLastLine: false,
            wordWrap: 'on'
        });
    }

    getCode(): string {
        return this.editor.getValue();
    }

    setCode(code: string): void {
        this.editor.setValue(code);
    }

    public dispose(): void {
        this.editor.dispose();
    }
}

// ---------
// Module Loading
// ---------
interface MonacoModules {
    Uri: typeof MonacoType.Uri | null;
    editor: typeof MonacoType.editor | null;
    languages: typeof MonacoType.languages | null;
}

let g_monacoModules: MonacoModules = {
    Uri: null,
    editor: null,
    languages: null
};

export async function loadMonacoModules(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const amdRequire = (window as any).require;

    return new Promise((resolve, reject) => {
        // 1. The 'require' call to fetch the main bundle
        amdRequire(['vs/editor/editor.main'], async () => {
            try {
                g_monacoModules.Uri = loadMonacoUriParser()!;
                g_monacoModules.editor = await loadMonacoEditor();
                g_monacoModules.languages = await loadMonacoLanguages();
                resolve();
            } catch (error) {
                reject(error);
            }
        },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (err: any) => {
                reject(err);
            });
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadMonacoUriParser(): typeof MonacoType.Uri | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const m = (window as any).monaco;
    if (!m) return null;

    // Check the internal location
    if (m.Uri) return m.Uri;

    // Check the editor namespace (some CDN builds put it here)
    if (m.editor && m.editor.Uri) return m.editor.Uri;

    return null;
}

async function loadMonacoEditor(): Promise<typeof MonacoType.editor> {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100; // 2 seconds total

        const check = (): void => {
            attempts++;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const m = (window as any).monaco;

            // We check for 'editor' and a core method like 'getModel' 
            // to ensure the namespace isn't just an empty object.
            if (m && m.editor && typeof m.editor.getModel === 'function') {
                resolve(m.editor);
                return;
            }

            if (attempts >= maxAttempts) {
                reject(new Error("Monaco Editor namespace failed to hydrate after 2 seconds."));
                return;
            }

            // Poll every 20ms
            setTimeout(check, 20);
        };

        // If the loader hasn't even started, we trigger it
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(window as any).require) {
            reject(new Error("Monaco Loader (loader.js) not found on page."));
            return;
        }

        // // Ensure the main module is requested
        // // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // (window as any).require(['vs/editor/editor.main'], check);
        check();
    });
}

async function loadMonacoLanguages(): Promise<typeof MonacoType.languages> {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100; // 2 seconds total

        const check = (): void => {
            attempts++;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const m = (window as any).monaco;

            // We check for 'editor' and a core method like 'getModel' 
            // to ensure the namespace isn't just an empty object.
            if (m && m.languages && typeof m.languages.registerCompletionItemProvider === 'function') {
                resolve(m.languages);
                return;
            }

            if (attempts >= maxAttempts) {
                reject(new Error("Monaco Language namespace failed to hydrate after 2 seconds."));
                return;
            }

            // Poll every 20ms
            setTimeout(check, 20);
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(window as any).require) {
            reject(new Error("Monaco Loader (loader.js) not found on page."));
            return;
        }

        check();
    });
}

// ----------
// Monaco Lua Intelligence
// ----------

// ------------
let g_monacoLuaIntelligenceInitialized = false; // lazy-init
let g_luaSchema: Record<string, Record<string, string>> = {};
let g_luaGlobals: Record<string, string> = {};
let g_friendlyNames: Record<string, string> = {};

function initializeMonacoLuaIntelligence(): void {
    if (g_monacoLuaIntelligenceInitialized) return;
    g_monacoLuaIntelligenceInitialized = true;

    const luaApiCompiled = LUA_API_DEFINITIONS.join('\n');

    initializeSchema(luaApiCompiled);
    setupHiddenLibrary(luaApiCompiled);
    registerLuaIntelligence();
}

function initializeSchema(apiSource: string): void {
    // Parse @class and @enum into a lookup table
    g_luaSchema = parseEmmyLua(apiSource);

    // Define hardcoded globals
    // Add Enums to the roots so they are accessible as top-level objects
    // const enumNames = apiSource.match(/---[ ]?@enum (\w+)/g)?.map(e => e.replace(/---[ ]?@enum /, '')) || [];
    // enumNames.forEach(name => {
    //     g_luaGlobals[name] = name;
    // });

    // Add Enums AND Aliases to the roots
    const rootRegex = /---[ ]?@(enum|alias) (\w+)/g;
    let match;
    while ((match = rootRegex.exec(apiSource)) !== null) {
        const name = match[2];
        if (name) g_luaGlobals[name] = name;
    }
}

function setupHiddenLibrary(apiSource: string): void {
    // In some AMD versions, the static methods are tucked inside a 'prototype' 
    // or require a direct call to the underlying constructor
    const UriParser = g_monacoModules.Uri!;
    const libUri = (typeof UriParser.parse === 'function')
        ? UriParser.parse("inmemory://model/lift-api.lua")
        : UriParser.from({ scheme: 'inmemory', path: 'model/lift-api.lua' });
    if (!g_monacoModules.editor!.getModel(libUri)) {
        g_monacoModules.editor!.createModel(apiSource, "lua", libUri);
    }
}

function registerLuaIntelligence(): void {
    g_monacoModules.languages!.registerCompletionItemProvider('lua', {
        triggerCharacters: ['.'],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        provideCompletionItems: (model: any, position: any): monaco.languages.ProviderResult<monaco.languages.CompletionList> => {
            const line = model.getLineContent(position.lineNumber).substring(0, position.column);
            const range = {
                startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
                startColumn: position.column, endColumn: position.column
            };

            const match = line.match(/([\w.\[\]"']+)\.$/);
            if (!match) return { suggestions: [] };

            const parts = match[1]!.split('.');
            const firstPartClean = parts[0]!.replace(/\[.*?\]/g, '');

            let currentType = g_luaGlobals[firstPartClean];
            if (!currentType) {
                const annotMatch = model.getValue().match(new RegExp(`---[ ]?@param\\s+${firstPartClean}\\s+(\\w+)`));
                if (annotMatch) currentType = annotMatch[1];
            }

            let lastResolvedAsCollection = false;

            for (let i = 1; i < parts.length; i++) {
                if (!currentType || !g_luaSchema[currentType]) break;

                const partRaw = parts[i]!;
                const partName = partRaw.replace(/\[.*?\]/g, '');
                const rawSchemaType = g_luaSchema[currentType]![partName];

                if (rawSchemaType) {
                    lastResolvedAsCollection = rawSchemaType.startsWith('COLLECT:');
                    currentType = rawSchemaType.replace('COLLECT:', '');
                } else {
                    currentType = undefined;
                    break;
                }
            }

            if (currentType && g_luaSchema[currentType]) {
                const lastPartRaw = parts[parts.length - 1]!;
                const isIndexed = lastPartRaw.includes('[');

                if (lastResolvedAsCollection && !isIndexed) {
                    return { suggestions: [] };
                }

                const cleanSuggestions = Object.keys(g_luaSchema[currentType]!).map(f => {
                    const rawType = g_luaSchema[currentType!]![f]!;
                    const isCollection = rawType.startsWith('COLLECT:');

                    // Strip the prefix for the actual logic and UI
                    const cleanDetailType = rawType.replace('COLLECT:', '');

                    // Check if this type name is one of our anonymous/inline types
                    let displayType = g_friendlyNames[cleanDetailType] || cleanDetailType;

                    // If it's a collection, wrap the friendly name or add brackets
                    if (isCollection) {
                        displayType = displayType.startsWith('{') ? `${displayType}[]` : `${displayType}[]`;
                    }

                    return {
                        label: f,
                        kind: g_luaGlobals[currentType!] ?
                            g_monacoModules.languages!.CompletionItemKind.EnumMember :
                            g_monacoModules.languages!.CompletionItemKind.Field,
                        insertText: f,
                        detail: displayType,
                        range,
                        sortText: `00_${f}` // This forces your specific API values to the TOP of the list
                    };
                });
                return { suggestions: cleanSuggestions };
            }
            return { suggestions: [] };
        }
    });
}

function parseEmmyLua(lib: string): Record<string, Record<string, string>> {
    const schema: Record<string, Record<string, string>> = {};

    let autoGenCounter = 0;

    const classBlocks = lib.split('--- @class');
    classBlocks.forEach(block => {
        const lines = block.trim().split('\n');
        const className = lines[0]?.split(' ')[0];
        if (!className) return;

        schema[className] = {};
        const fieldRegex = /--- @field\s+(\w+)\s+(.+)/g;
        let match;
        while ((match = fieldRegex.exec(block)) !== null) {
            const fieldName = match[1]!;
            let rawType = match[2]!.trim();

            // Check for inline table: { key: type, key2: type }
            const inlineMatch = rawType.match(/\{(.*)\}/);
            if (inlineMatch) {
                const genClassName = `AnonymousType${++autoGenCounter}`;
                // Store the friendly version: "{ visitors: string[] }"
                g_friendlyNames[genClassName] = inlineMatch[0];

                schema[genClassName] = {};
                const inlineBody = inlineMatch[1]!;
                inlineBody.split(',').forEach(pair => {
                    const [k, v] = pair.split(':').map(s => s.trim());
                    if (k && v) {
                        const isCol = v.includes('table<') || v.includes('[]');
                        const cleanV = v.replace(/table<.*,\s*(.*)>/, '$1').replace(/\[\]/g, '').trim();
                        schema[genClassName]![k] = isCol ? `COLLECT:${cleanV}` : cleanV;
                    }
                });

                const isParentCol = rawType.includes('table<') || rawType.includes('[]');
                schema[className][fieldName] = isParentCol ? `COLLECT:${genClassName}` : genClassName;
                continue;
            }

            // Standard parsing for non-inline types
            const isCollection = rawType.includes('table<') || rawType.includes('[]');
            let cleanType = rawType
                .replace(/table<[^,]+,\s*([^>]+)>/, '$1')
                .replace(/\[\]/g, '')
                .replace(/[?|]/g, '')
                .trim();

            schema[className][fieldName] = isCollection ? `COLLECT:${cleanType}` : cleanType;
        }
    });

    // 2. Parse Enums (Keep your existing enum logic here)
    const enumBlocks = lib.split('--- @enum');
    enumBlocks.forEach(block => {
        const lines = block.trim().split('\n');
        const enumName = lines[0]?.trim();
        if (!enumName) return;
        schema[enumName] = {};
        const enumValueRegex = /(\w+)\s*=\s*[\d"']+/g;
        let match;
        while ((match = enumValueRegex.exec(block)) !== null) {
            schema[enumName][match[1]!] = 'number';
        }
    });

    // 3. Parse Aliases (e.g., --- @alias ButtonType "'SingleCall'" | "'UpDown'")
    const aliasBlocks = lib.split('--- @alias');
    aliasBlocks.forEach(block => {
        const lines = block.trim().split('\n');
        const firstLine = lines[0];
        if (!firstLine) return;

        const aliasName = firstLine.split(' ')[0];
        if (!aliasName) return;

        schema[aliasName] = {};

        // Find all quoted strings like "'SingleCall'"
        const optionRegex = /"([^"]+)"|'([^']+)'/g;
        let match;
        while ((match = optionRegex.exec(firstLine)) !== null) {
            const value = match[1] || match[2];
            if (value) {
                // We map the value to 'string' so the resolver sees it as a valid field
                schema[aliasName][value] = 'string';
            }
        }
    });

    return schema;
}